import { useMemo, useState } from 'react';
import { CITISALUD_LOGO } from './logo';

const nf = (n) => Number(n || 0).toLocaleString('es-CO');
const pf = (x) => ((x || 0) * 100).toFixed(1).replace('.', ',') + ' %';
const NOMBRES = {
  'UNIDAD MOVIL': 'Unidad móvil', 'SEDE TURBACO': 'Sede Turbaco', 'DOMICILIO': 'Domicilio',
  'URBANO': 'Urbano', 'RURAL': 'Rural', 'NO CONTACTADA': 'No contactada', 'CONTACTADA': 'Contactada',
  'NUMERO ERRADO': 'Número errado', 'CONTESTA FAMILIAR': 'Contesta familiar',
  'APTA': 'Apta', 'NO APTA': 'No apta',
};
const lindo = (s) => (!s ? s : NOMBRES[s] || (s.length <= 3 ? s : s[0] + s.slice(1).toLowerCase()));
const dm = (f) => (f ? f.slice(8, 10) + '/' + f.slice(5, 7) : '');

// Cita agendada = registro cuya columna de asignación (AJ) trae DOMICILIO,
// SEDE TURBACO o UNIDAD MÓVIL. El dato puede quedar en `cita` (texto) o en
// `modalidad` según el encabezado; se acepta cualquier variante de escritura.
const sinT = (s) => String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();
const esCitaVal = (v) => { const s = sinT(v); return s.includes('DOMICILIO') || s.includes('TURBACO') || s.includes('UNIDAD') || s.includes('MOVIL'); };
const esCita = (r) => esCitaVal(r.modalidad) || esCitaVal(r.cita_texto);

/* --- little inline SVG icons for the KPI cards --- */
const IC = {
  users: 'M9 8a3.2 3.2 0 1 0 0-.01M3.5 20a5.5 5.5 0 0 1 11 0M17 8.2a3 3 0 0 1 0 5.6M18.5 20a5 5 0 0 0-3-4.6',
  phone: 'M4 5c0 8 7 15 15 15l0-3-4-1-2 2c-3-1.5-5.5-4-7-7l2-2-1-4z',
  cal: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4M9 15l2 2 4-4',
  target: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M12 12h.01',
  check: 'M6 4h10l2 2v14H6zM9 12l2 2 4-4',
};
function Icon({ d, color }) {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke={color}
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {d.split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
    </svg>
  );
}

/* --- sparkline from a numeric series --- */
function Spark({ vals, color }) {
  if (!vals || vals.length < 2) return <div style={{ height: 22, marginTop: 6 }} />;
  const max = Math.max(...vals), min = Math.min(...vals), range = (max - min) || 1;
  const pts = vals.map((v, i) =>
    `${(i / (vals.length - 1)) * 100},${20 - ((v - min) / range) * 16}`).join(' ');
  return (
    <svg width="100%" height="22" viewBox="0 0 100 22" preserveAspectRatio="none"
         style={{ marginTop: 6, overflow: 'visible' }} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* --- generic donut; segs = [{v,color}] --- */
function Donut({ segs, size = 108, sw = 6, centro, sub }) {
  const total = segs.reduce((a, s) => a + s.v, 0) || 1;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 42 42" aria-hidden="true" style={{ flex: 'none' }}>
      <circle cx="21" cy="21" r="15.9155" fill="none" stroke="var(--track)" strokeWidth={sw} />
      {segs.map((s, i) => {
        const p = (s.v / total) * 100;
        const el = (
          <circle key={i} cx="21" cy="21" r="15.9155" fill="none" stroke={s.color} strokeWidth={sw}
                  strokeDasharray={`${p} ${100 - p}`} strokeDashoffset={25 - acc}
                  transform="rotate(-90 21 21)" style={{ transition: 'stroke-dasharray .5s' }} />
        );
        acc += p;
        return el;
      })}
      {centro && <text x="21" y={sub ? 20 : 23} textAnchor="middle" fontSize="6.5" fontWeight="800" fill="var(--ink)">{centro}</text>}
      {sub && <text x="21" y="26" textAnchor="middle" fontSize="3.2" fill="var(--ink-3)">{sub}</text>}
    </svg>
  );
}

/* --- solid pie chart (filled wedges, no hole) --- */
function Pie({ segs, size = 132, centro }) {
  const total = segs.reduce((a, s) => a + s.v, 0) || 1;
  const cx = 21, cy = 21, r = 20;
  let a0 = -Math.PI / 2;
  const pt = (ang) => [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
  return (
    <svg width={size} height={size} viewBox="0 0 42 42" aria-hidden="true" style={{ flex: 'none' }}>
      {segs.map((s, i) => {
        const frac = s.v / total;
        if (frac <= 0) return null;
        if (frac >= 1) return <circle key={i} cx={cx} cy={cy} r={r} fill={s.color} />;
        const a1 = a0 + frac * 2 * Math.PI;
        const [x0, y0] = pt(a0); const [x1, y1] = pt(a1);
        const large = frac > 0.5 ? 1 : 0;
        const d = `M${cx},${cy} L${x0.toFixed(3)},${y0.toFixed(3)} A${r},${r} 0 ${large} 1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
        a0 = a1;
        return <path key={i} d={d} fill={s.color} />;
      })}
      {centro && <text x="21" y="23.5" textAnchor="middle" fontSize="6.2" fontWeight="800" fill="#fff" style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,.25)', strokeWidth: 0.6 }}>{centro}</text>}
    </svg>
  );
}

export default function Tablero({ registros, campana }) {
  const [f, setF] = useState({ agente: 'Todos', dia: 'Todos', zona: 'Todos', estado: null });

  const opciones = useMemo(() => ({
    agentes: [...new Set(registros.map((r) => r.agente).filter(Boolean))].sort(),
    dias: [...new Set(registros.map((r) => r.fecha_gestion).filter(Boolean))].sort(),
    zonas: [...new Set(registros.map((r) => r.zona).filter(Boolean))].sort(),
  }), [registros]);

  const base = useMemo(() => registros.filter((r) =>
    (f.agente === 'Todos' || r.agente === f.agente) &&
    (f.dia === 'Todos' || r.fecha_gestion === f.dia) &&
    (f.zona === 'Todos' || r.zona === f.zona)
  ), [registros, f.agente, f.dia, f.zona]);

  // same filters but ignoring the day, so the sparklines always show a trend
  const baseSinDia = useMemo(() => registros.filter((r) =>
    (f.agente === 'Todos' || r.agente === f.agente) &&
    (f.zona === 'Todos' || r.zona === f.zona)
  ), [registros, f.agente, f.zona]);

  const vista = useMemo(() => base.filter((r) => !f.estado || r.estado_contacto === f.estado), [base, f.estado]);

  const k = useMemo(() => {
    const gest = vista.length;
    const cont = vista.filter((r) => r.estado_contacto === 'CONTACTADA').length;
    const cf = vista.filter((r) => r.estado_contacto === 'CONTESTA FAMILIAR').length;
    // "Citas agendadas" = suma de DOMICILIO + SEDE TURBACO + UNIDAD MÓVIL en la
    // columna de asignación (AJ).
    const citas = vista.filter(esCita).length;
    const contactados = cont + cf; // contactada + contesta familiar
    const diasVista = f.dia === 'Todos' ? (opciones.dias.length || 1) : 1;
    return {
      gest, cont, cf, citas, contactados, diasVista,
      contactabilidad: gest ? contactados / gest : 0,   // (contactada + contesta familiar) / gestionados
      efectividad: contactados ? citas / contactados : 0, // citas asignadas / contactados
    };
  }, [vista, f.dia, opciones.dias.length]);

  // daily series for the KPI sparklines + deltas
  const series = useMemo(() => {
    const d = opciones.dias;
    const cnt = (pred) => d.map((dia) => baseSinDia.filter((r) => r.fecha_gestion === dia && pred(r)).length);
    return {
      gest: d.map((dia) => baseSinDia.filter((r) => r.fecha_gestion === dia).length),
      cont: cnt((r) => r.estado_contacto === 'CONTACTADA'),
      citas: cnt(esCita),
    };
  }, [baseSinDia, opciones.dias]);
  const delta = (arr) => {
    if (!arr || arr.length < 2) return null;
    const a = arr[arr.length - 2], b = arr[arr.length - 1];
    if (!a) return null;
    return Math.round(((b - a) / a) * 100);
  };

  const estados = ['CONTACTADA', 'NO CONTACTADA', 'NUMERO ERRADO', 'CONTESTA FAMILIAR']
    .map((e) => ({ e, v: base.filter((r) => r.estado_contacto === e).length }));
  const estadoColor = { CONTACTADA: 'var(--verde)', 'NO CONTACTADA': 'var(--azul)', 'NUMERO ERRADO': 'var(--naranja)', 'CONTESTA FAMILIAR': 'var(--violeta)' };
  const totalEstado = estados.reduce((a, x) => a + x.v, 0) || 1;

  const porFecha = useMemo(() => {
    const m = {};
    vista.filter((r) => r.tiene_cita).forEach((r) => {
      const key = r.cita_fecha || 'SIN FECHA';
      m[key] = (m[key] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) =>
      a[0] === 'SIN FECHA' ? 1 : b[0] === 'SIN FECHA' ? -1 : a[0].localeCompare(b[0]));
  }, [vista]);
  const maxFecha = Math.max(1, ...porFecha.map(([, v]) => v));

  const agentes = useMemo(() => opciones.agentes.map((a) => {
    const sub = base.filter((r) => r.agente === a);
    return { a, g: sub.length, c: sub.filter((r) => r.estado_contacto === 'CONTACTADA').length,
             k: sub.filter(esCita).length };
  }).sort((x, y) => y.k - x.k), [base, opciones.agentes]);
  const maxAgK = Math.max(1, ...agentes.map((x) => x.k));

  const modalidades = useMemo(() => {
    const m = {};
    vista.filter((r) => r.modalidad).forEach((r) => { m[r.modalidad] = (m[r.modalidad] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [vista]);
  const maxMod = Math.max(1, ...modalidades.map(([, v]) => v));

  const chip = (grupo, valor, actual) => (
    <button key={grupo + valor} type="button"
      onClick={() => setF({ ...f, [grupo]: valor, estado: null })}
      style={{ borderRadius: 999, padding: '5px 12px', fontSize: 12.5,
               background: actual === valor ? 'var(--azul-deep)' : 'var(--surface-2)',
               borderColor: actual === valor ? 'var(--azul-deep)' : 'var(--line)',
               color: actual === valor ? '#fff' : 'var(--ink-2)',
               fontWeight: actual === valor ? 700 : 400 }}>
      {grupo === 'dia' && valor !== 'Todos' ? dm(valor) : lindo(valor)}
    </button>
  );

  const KPIS = [
    { lab: 'Gestionados', val: nf(k.gest), foot: 'registros trabajados', ic: IC.users, color: 'var(--azul)', bg: 'rgba(74,159,224,.16)', s: series.gest, d: delta(series.gest) },
    { lab: 'Contactadas', val: nf(k.cont), foot: k.gest ? pf(k.cont / k.gest) + ' de contacto' : '—', ic: IC.phone, color: 'var(--azul)', bg: 'rgba(74,159,224,.16)', s: series.cont, d: delta(series.cont) },
    { lab: 'Citas agendadas', val: nf(k.citas), foot: 'columna de asignación diligenciada', ic: IC.cal, color: 'var(--verde)', bg: 'rgba(18,201,142,.16)', s: series.citas, d: delta(series.citas), hi: true },
  ];

  return (
    <>
      {/* hero */}
      <div className="hero">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <img src={CITISALUD_LOGO} alt="CitiSalud" style={{ height: 46, background: '#fff', borderRadius: 12, padding: '8px 14px', boxShadow: '0 6px 18px -8px rgba(0,0,0,.5)' }} />
            <div>
              <div className="eyebrow">{campana?.organizacion || 'Campaña'}</div>
              <h1>{campana?.nombre || 'Tablero de campaña'}</h1>
              <div className="sub">Así va tu campaña de tamizaje hoy.</div>
            </div>
          </div>
          <span className="chiplive">● En vivo · {nf(k.gest)} registros en vista</span>
        </div>
      </div>

      {/* filters */}
      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 14 }}>
        {[['Agente', 'agente', ['Todos', ...opciones.agentes]],
          ['Día', 'dia', ['Todos', ...opciones.dias]],
          ['Zona', 'zona', ['Todos', ...opciones.zonas]]].map(([et, grupo, vals]) => (
          <div key={grupo} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="mut" style={{ textTransform: 'uppercase', letterSpacing: '.1em', fontSize: 10.5, fontWeight: 700 }}>{et}</span>
            {vals.map((v) => chip(grupo, v, f[grupo]))}
          </div>
        ))}
        <button style={{ marginLeft: 'auto' }} onClick={() => setF({ agente: 'Todos', dia: 'Todos', zona: 'Todos', estado: null })}>Limpiar</button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12, marginBottom: 14 }}>
        {KPIS.map((c) => (
          <div className="kpi" key={c.lab} style={c.hi ? { background: 'linear-gradient(160deg,var(--verde-deep),#0a6e51)', borderColor: 'transparent' } : undefined}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="lab" style={c.hi ? { color: 'rgba(255,255,255,.8)' } : undefined}>{c.lab}</div>
                <div className="val" style={c.hi ? { color: '#fff' } : undefined}>{c.val}</div>
                <div className="foot" style={c.hi ? { color: 'rgba(255,255,255,.8)' } : undefined}>{c.foot}</div>
              </div>
              <div className="ic" style={{ background: c.hi ? 'rgba(255,255,255,.16)' : c.bg }}>
                <Icon d={c.ic} color={c.hi ? '#fff' : c.color} />
              </div>
            </div>
            <Spark vals={c.s} color={c.hi ? 'rgba(255,255,255,.7)' : c.color} />
            {c.d != null && <div className={'delta ' + (c.d >= 0 ? 'up' : 'dn')} style={c.hi ? { color: 'rgba(255,255,255,.9)' } : undefined}>{c.d >= 0 ? '↑' : '↓'} {Math.abs(c.d)}% vs. día anterior</div>}
            {c.meta != null && <div className="delta" style={{ color: c.meta ? 'var(--verde)' : 'var(--ambar)' }}>{c.meta ? '● En meta' : '● Bajo meta'}</div>}
          </div>
        ))}
      </div>

      {/* NIVEL 1: contactabilidad (pie) | estado del contacto */}
      <div className="grid" style={{ marginBottom: 14 }}>
        <section className="card c6">
          <h2 className="ctitle">Contactabilidad</h2>
          <p className="cnote">(Contactada + contesta familiar) ÷ gestionados</p>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <Pie size={140}
                 segs={[{ v: k.contactados, color: 'var(--verde)' }, { v: Math.max(k.gest - k.contactados, 0), color: 'var(--track)' }]}
                 centro={pf(k.contactabilidad).replace(' %', '%')} />
            <div className="legend" style={{ flex: 1 }}>
              <div className="li"><span><i className="dot" style={{ background: 'var(--verde)' }} />Contactadas</span><b>{nf(k.cont)}</b></div>
              <div className="li"><span><i className="dot" style={{ background: 'var(--verde)' }} />Contesta familiar</span><b>{nf(k.cf)}</b></div>
              <div className="li"><span className="mut">Contactados</span><b>{nf(k.contactados)}</b></div>
              <div className="li"><span><i className="dot" style={{ background: 'var(--track)' }} />Gestionados</span><b>{nf(k.gest)}</b></div>
            </div>
          </div>
        </section>

        <section className="card c6">
          <h2 className="ctitle">Estado del contacto</h2>
          <p className="cnote">Clic en un estado para aislarlo</p>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Donut size={140} segs={estados.map(({ e, v }) => ({ v, color: estadoColor[e] }))} centro={nf(totalEstado)} sub="total" />
            <div className="legend" style={{ flex: 1 }}>
              {estados.map(({ e, v }) => (
                <div key={e} className="li" style={{ cursor: 'pointer', opacity: f.estado && f.estado !== e ? .4 : 1 }}
                     onClick={() => setF({ ...f, estado: f.estado === e ? null : e })}>
                  <span><i className="dot" style={{ background: estadoColor[e] }} />{lindo(e)}</span>
                  <b>{Math.round(v / totalEstado * 100)}%</b>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* NIVEL 2: efectividad | citas por fecha | modalidad */}
      <div className="grid" style={{ marginBottom: 14 }}>
        <section className="card c3">
          <h2 className="ctitle">Efectividad</h2>
          <p className="cnote">Citas asignadas ÷ contactados</p>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <Donut sw={7} size={100}
                   segs={[{ v: k.citas, color: 'var(--verde)' }, { v: Math.max(k.contactados - k.citas, 0), color: 'var(--track)' }]}
                   centro={pf(k.efectividad).replace(' %', '%')} />
            <div className="legend">
              <div className="li"><span><i className="dot" style={{ background: 'var(--verde)' }} />Citas asignadas</span><b>{nf(k.citas)}</b></div>
              <div className="li"><span><i className="dot" style={{ background: 'var(--ink-3)' }} />Contactados</span><b>{nf(k.contactados)}</b></div>
            </div>
          </div>
        </section>

        <section className="card c5">
          <h2 className="ctitle">Citas por fecha de atención</h2>
          <p className="cnote">Programación de la toma de muestra</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 160, borderBottom: '1px solid var(--line)' }}>
            {porFecha.map(([fecha, v]) => (
              <div key={fecha} title={`${fecha === 'SIN FECHA' ? 'Sin fecha fija' : dm(fecha)}: ${v} citas`}
                   style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 4, minWidth: 0 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-2)' }}>{v}</div>
                <div style={{ width: '100%', height: v / maxFecha * 124, minHeight: 3, borderRadius: '5px 5px 0 0',
                              background: fecha === 'SIN FECHA' ? 'var(--track)' : 'linear-gradient(var(--verde),var(--verde-deep))',
                              transition: 'height .5s cubic-bezier(.22,.8,.25,1)' }} />
              </div>
            ))}
            {!porFecha.length && <p className="mut" style={{ margin: 'auto' }}>Sin citas en esta vista</p>}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
            {porFecha.map(([fecha]) => (
              <div key={fecha} style={{ flex: '1 1 0', textAlign: 'center', fontSize: 9.5, color: 'var(--ink-3)', overflow: 'hidden' }}>
                {fecha === 'SIN FECHA' ? 'S/F' : dm(fecha)}
              </div>
            ))}
          </div>
        </section>

        <section className="card c4">
          <h2 className="ctitle">Modalidad preferida</h2>
          <p className="cnote">Dónde prefieren ser tamizadas</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {modalidades.map(([m, v]) => (
              <div key={m}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span>{lindo(m)}</span><b>{nf(v)}</b>
                </div>
                <div style={{ height: 9, background: 'var(--track)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${v / maxMod * 100}%`, background: 'var(--azul)', borderRadius: 5, transition: 'width .5s' }} />
                </div>
              </div>
            ))}
            {!modalidades.length && <p className="mut">Sin datos de modalidad</p>}
          </div>
        </section>
      </div>

      {/* NIVEL 3: actividad de agentes */}
      <div className="grid">
        <section className="card c12">
          <div className="chead"><h2 className="ctitle">Actividad de agentes</h2><span style={{ fontSize: 10, color: 'var(--verde)', fontWeight: 700 }}>● En vivo</span></div>
          <p className="cnote">Clic en un agente para filtrar</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '11px 22px', marginTop: 4 }}>
            {agentes.map((x) => (
              <div key={x.a} className="agrow" style={{ cursor: 'pointer', opacity: f.agente !== 'Todos' && f.agente !== x.a ? .4 : 1 }}
                   onClick={() => setF({ ...f, agente: f.agente === x.a ? 'Todos' : x.a, estado: null })}
                   title={`${x.a}: ${nf(x.g)} gestionados · ${x.c} contactadas · ${x.k} citas`}>
                <div><b>{x.a}</b><div style={{ color: 'var(--ink-3)', fontSize: 10 }}>{nf(x.k)} citas · {nf(x.g)} gest.</div></div>
                <div style={{ height: 7, background: 'var(--track)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${x.k / maxAgK * 100}%`, background: 'linear-gradient(90deg,var(--azul),var(--verde))', borderRadius: 4, transition: 'width .5s' }} />
                </div>
                <span style={{ textAlign: 'right', color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}>{x.g ? Math.round(x.k / x.g * 100) : 0}%</span>
              </div>
            ))}
            {!agentes.length && <p className="mut">Sin agentes en esta vista</p>}
          </div>
        </section>
      </div>
    </>
  );
}

